import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import AsyncSelect from "react-select/async";

import { useDarkMode } from "../../../../context/ThemeContext";
import { useDebounce } from "../../../../hooks/useDebounce";
import BackButton from "../../components/BackButton";
import {
  fetchEquipoLookups,
  fetchDevice,
  saveDevice,
  deleteDevice,
  fetchV5DeviceByImei,
  saveV5FuelSensor,
} from "../../../../features/sg/equipos";
import {
  fuelSensorErrors,
  fuelSensorPayload,
  rawValue,
  type FuelSensor,
  type FuelSensorInput,
} from "../../../../Layouts/admin/components/sections/devices/utils/fuelSensor";
import { fuelSensorChanged } from "../../../../features/sg/fuelSensor";
import { emptyEquipoForm, toEquipoForm } from "../../../../features/sg/schemas";
import { searchUsers, fetchUserOption } from "../../../../features/sg/usuarios";
import {
  notifyOk,
  notifyError,
  notifyLoading,
  confirmAction,
} from "../../../../features/sg/notify";
import { pickBackupSources } from "../../../../features/device-archive/backupModal";

import {
  FormContainer,
  Path,
  ButtonsContainer,
  Form,
  SectionsContainer,
  Section,
  SectionTitle,
  SectionContainer,
  InputContainer,
  InputLabel,
  Input,
  Error as ErrorLabel,
  Button,
} from "./styles";
import { getCustomSelectStyles } from "../../../../Layouts/admin/shared/theme/selectStyles";

export default function DevicesForm() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation("sg");
  const { isDarkMode } = useDarkMode();

  const inputClass = isDarkMode
    ? "bg-[#232323] text-white border-dark-border"
    : "bg-white text-gray-900 border-[#DFE5EF]";
  const labelClass = isDarkMode ? "text-white" : "text-gray-500";
  const sectionSpan = isDarkMode
    ? "px-2 bg-[#232323] text-white"
    : "px-2 bg-white text-[#757575]";

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({ defaultValues: emptyEquipoForm() });

  const [userOption, setUserOption] = useState<any>(null);
  const watchedStatusId = useWatch({ control, name: "status_id" });
  const watchedBrandId = useWatch({ control, name: "brand_id" });

  // Sensor de combustible: vive en el device de v5 con este IMEI (mismo campo
  // fuel_sensor que Administración → Dispositivos). Form aparte para que el
  // reset del equipo de SG al recargar no borre lo capturado.
  const watchedImei = useWatch({ control, name: "imei" });
  const imeiForV5 = useDebounce(String(watchedImei ?? "").trim(), 500);
  const { data: v5Device, isFetching: v5Loading } = useQuery({
    queryKey: ["sg-v5-device", imeiForV5],
    queryFn: () => fetchV5DeviceByImei(imeiForV5),
    enabled: imeiForV5.length > 0,
  });
  const fuelForm = useForm<{ fuel_sensor: FuelSensorInput }>({
    defaultValues: { fuel_sensor: {} },
  });
  const fuelErrors = fuelForm.formState.errors.fuel_sensor;
  useEffect(() => {
    fuelForm.reset({ fuel_sensor: v5Device?.fuel_sensor ?? {} });
  }, [v5Device, fuelForm]);

  const { data: lookups } = useQuery({
    queryKey: ["sg-equipo-lookups"],
    queryFn: fetchEquipoLookups,
  });

  const statusOptions = (lookups?.statuses ?? [])
    .filter((s: any) =>
      ["activo", "inactivo"].includes((s.name_es || s.name || "").toLowerCase())
    )
    .map((s: any) => ({ value: s.id, label: s.name_es || s.name }));
  const conditionOptions = (lookups?.conditions ?? [])
    .filter((c: any) => String(c.status_id) === String(watchedStatusId))
    .map((c: any) => ({ value: c.id, label: c.name }));
  const carImageOptions = (lookups?.carImages ?? []).map((c: any) => ({
    value: c.id,
    label: c.name,
  }));
  const brandRows = lookups?.brands ?? [];
  const brandOptions = brandRows.map((b: any) => {
    const ip = b.network?.ip_address;
    const port = b.network?.port;
    const net = ip && port ? `${ip}:${port}` : ip || port || null;
    return {
      value: b.id,
      label: [b.brand, b.model, net].filter(Boolean).join(" · "),
    };
  });
  const selectedBrand =
    brandRows.find((b: any) => String(b.id) === String(watchedBrandId)) || null;

  const { data: deviceData } = useQuery({
    queryKey: ["sg-device-edit", id],
    queryFn: () => fetchDevice(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (!deviceData) return;
    reset(toEquipoForm(deviceData) as any);
    if (deviceData.user_id) {
      fetchUserOption(deviceData.user_id).then(setUserOption);
    }
  }, [deviceData, reset]);

  useEffect(() => {
    const current = getValues("device_condition_id");
    if (!current) return;
    const cond = (lookups?.conditions ?? []).find(
      (c: any) => String(c.id) === String(current)
    );
    if (cond && String(cond.status_id) !== String(watchedStatusId)) {
      setValue("device_condition_id", "");
    }
  }, [watchedStatusId, lookups, getValues, setValue]);

  const saveMutation = useMutation({
    mutationFn: async (values: any) => {
      const deviceId = await saveDevice({ id, isNew, values });
      if (v5Device) {
        const fs = fuelSensorPayload(
          fuelForm.getValues("fuel_sensor"),
          !!v5Device.fuel_sensor
        );
        if (fuelSensorChanged(fs, v5Device.fuel_sensor)) {
          try {
            await saveV5FuelSensor(v5Device.id, fs as FuelSensor | null);
          } catch (err: any) {
            throw new Error(
              t("deviceForm.fuel.saveError", {
                msg: err?.response?.data?.message || err.message,
              })
            );
          }
        }
      }
      return deviceId;
    },
    onSuccess: (deviceId) => {
      queryClient.invalidateQueries({ queryKey: ["sg-v5-device"] });
      queryClient.invalidateQueries({ queryKey: ["sg-devices"] });
      queryClient.invalidateQueries({ queryKey: ["sg-barrido"] });
      queryClient.invalidateQueries({ queryKey: ["sg-counts"] });
      if (!isNew)
        queryClient.invalidateQueries({ queryKey: ["sg-device-edit", id] });
      notifyOk();
      if (isNew) navigate(`/sg/devices/edit/${deviceId}`, { replace: true });
    },
    onError: (err: any) => notifyError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (opts?: { backupSources?: string[]; purgeOnly?: boolean }) =>
      deleteDevice(id!, opts ?? {}),
    onMutate: () => {
      notifyLoading(t("deviceForm.deleting"));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sg-devices"] });
      queryClient.invalidateQueries({ queryKey: ["sg-barrido"] });
      queryClient.invalidateQueries({ queryKey: ["sg-counts"] });
      notifyOk(t("deviceForm.deleted"));
      navigate("/sg/devices", { replace: true });
    },
    onError: (err: any) => notifyError(err.message),
  });

  async function onDelete() {
    if (userOption?.v5) {
      const choice = await pickBackupSources("delete", { entity: "device" });
      if (choice.action === "cancel") return;
      deleteMutation.mutate(
        choice.action === "archive"
          ? { backupSources: choice.sources }
          : { purgeOnly: true },
      );
      return;
    }
    const ok = await confirmAction({
      title: t("deviceForm.deleteTitle"),
      text: t("common.confirmText"),
      confirmText: t("deviceForm.deleteConfirm"),
    });
    if (ok) deleteMutation.mutate(undefined);
  }

  const saving = saveMutation.isPending;
  const deleting = deleteMutation.isPending;

  return (
    <FormContainer>
      <Path className="text-gray-400">
        {isNew ? t("deviceForm.breadcrumbNew") : t("deviceForm.breadcrumbEdit")}
      </Path>
      <Form
        onSubmit={handleSubmit(async (v) => {
          if (v5Device && !(await fuelForm.trigger())) return;
          saveMutation.mutate(v);
        })}
      >
        <SectionsContainer className={isDarkMode ? "bg-[#232323]" : "bg-white"}>
          <div className="flex gap-5">
            <BackButton handleClick={() => navigate("/sg/devices")} />
            <span className="text-[#757575] font-semibold">
              {t("common.back")}
            </span>
          </div>
          <div className="w-full mb-2 text-center">
            <h1
              className={
                isDarkMode
                  ? "text-2xl font-bold text-white w-full"
                  : "text-2xl font-bold text-primary-500 w-full"
              }
            >
              {isNew ? t("deviceForm.titleNew") : t("deviceForm.titleEdit")}
            </h1>
            <div
              className={
                isDarkMode
                  ? "w-full h-0.5 bg-dark-border mt-2 rounded-full"
                  : "w-full h-0.5 bg-[#DFE5EF] mt-2 rounded-full"
              }
            ></div>
          </div>

          {userOption?.v5 && (
            <div
              className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${
                isDarkMode
                  ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
                  : "border-amber-300 bg-amber-50 text-amber-800"
              }`}
            >
              <i className="ri-links-line mt-0.5 text-lg"></i>
              <div>
                <p className="text-sm font-semibold">
                  {t("deviceForm.v5Notice.title")}
                </p>
                <p className="mt-0.5 text-xs">
                  {isNew
                    ? t("deviceForm.v5Notice.textNew")
                    : t("deviceForm.v5Notice.text")}
                </p>
              </div>
            </div>
          )}

          <Section>
            <SectionTitle>
              <span className={sectionSpan}>
                {t("deviceForm.sections.info")}
              </span>
            </SectionTitle>
            <SectionContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fields.uniqId")} *
                </InputLabel>
                <Input
                  className={inputClass}
                  {...register("uniq_id", { required: true })}
                />
                {errors.uniq_id && <ErrorLabel>{t("common.required")}</ErrorLabel>}
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fields.name")} *
                </InputLabel>
                <Input
                  className={inputClass}
                  {...register("name", { required: true })}
                />
                {errors.name && <ErrorLabel>{t("common.required")}</ErrorLabel>}
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fields.user")} *
                </InputLabel>
                <Controller
                  name="user_id"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      isClearable
                      styles={getCustomSelectStyles(isDarkMode) as any} menuPortalTarget={document.body} menuPosition="fixed"
                      placeholder={t("deviceForm.placeholders.user")}
                      loadOptions={(input: string) => searchUsers(input)}
                      value={userOption}
                      onChange={(opt: any) => {
                        setUserOption(opt);
                        field.onChange(opt ? opt.value : "");
                      }}
                    />
                  )}
                />
                {errors.user_id && <ErrorLabel>{t("common.required")}</ErrorLabel>}
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fields.status")} *
                </InputLabel>
                <Controller
                  name="status_id"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      options={statusOptions}
                      styles={getCustomSelectStyles(isDarkMode) as any} menuPortalTarget={document.body} menuPosition="fixed"
                      placeholder={t("deviceForm.placeholders.select")}
                      value={
                        statusOptions.find(
                          (o: any) => String(o.value) === String(field.value)
                        ) ?? null
                      }
                      onChange={(opt: any) =>
                        field.onChange(opt ? opt.value : "")
                      }
                    />
                  )}
                />
                {errors.status_id && <ErrorLabel>{t("common.required")}</ErrorLabel>}
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fields.tag")}
                </InputLabel>
                <Controller
                  name="device_condition_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      isClearable
                      options={conditionOptions}
                      styles={getCustomSelectStyles(isDarkMode) as any} menuPortalTarget={document.body} menuPosition="fixed"
                      placeholder={t("deviceForm.placeholders.tag")}
                      value={
                        conditionOptions.find(
                          (o: any) => String(o.value) === String(field.value)
                        ) ?? null
                      }
                      onChange={(opt: any) =>
                        field.onChange(opt ? opt.value : "")
                      }
                    />
                  )}
                />
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fields.image")}
                </InputLabel>
                <Controller
                  name="car_image_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      isClearable
                      options={carImageOptions}
                      styles={getCustomSelectStyles(isDarkMode) as any} menuPortalTarget={document.body} menuPosition="fixed"
                      placeholder={t("deviceForm.placeholders.select")}
                      value={
                        carImageOptions.find(
                          (o: any) => String(o.value) === String(field.value)
                        ) ?? null
                      }
                      onChange={(opt: any) =>
                        field.onChange(opt ? opt.value : "")
                      }
                    />
                  )}
                />
              </InputContainer>
              <InputContainer className="col-span-full">
                <InputLabel className={labelClass}>
                  {t("deviceForm.fields.brandModel")}
                </InputLabel>
                <Controller
                  name="brand_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      isClearable
                      options={brandOptions}
                      styles={getCustomSelectStyles(isDarkMode) as any} menuPortalTarget={document.body} menuPosition="fixed"
                      placeholder={t("deviceForm.placeholders.brandModel")}
                      value={
                        brandOptions.find(
                          (o: any) => String(o.value) === String(field.value)
                        ) ?? null
                      }
                      onChange={(opt: any) =>
                        field.onChange(opt ? opt.value : "")
                      }
                    />
                  )}
                />
              </InputContainer>
              {selectedBrand && (
                <div
                  className={`col-span-full grid grid-cols-2 sm:grid-cols-5 gap-3 rounded-lg p-3 text-sm border ${
                    isDarkMode
                      ? "bg-[#1c1c1c] border-dark-border"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  {[
                    {
                      label: t("deviceForm.brandPanel.brand"),
                      value: selectedBrand.brand,
                    },
                    {
                      label: t("deviceForm.brandPanel.model"),
                      value: selectedBrand.model,
                    },
                    {
                      label: t("deviceForm.brandPanel.ip"),
                      value: selectedBrand.network?.ip_address,
                    },
                    {
                      label: t("deviceForm.brandPanel.port"),
                      value: selectedBrand.network?.port,
                    },
                    {
                      label: t("deviceForm.brandPanel.protocol"),
                      value: selectedBrand.network?.protocol,
                    },
                  ].map((item) => (
                    <div key={item.label}>
                      <span
                        className={`block text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {item.label}
                      </span>
                      <b
                        className={`block ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {item.value || "—"}
                      </b>
                    </div>
                  ))}
                </div>
              )}
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fields.simcard")}
                </InputLabel>
                <Input className={inputClass} {...register("simcard")} />
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fields.phone")}
                </InputLabel>
                <Input className={inputClass} {...register("phone")} />
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fields.imei")} *
                </InputLabel>
                <Input
                  className={inputClass}
                  {...register("imei", { required: true })}
                />
                {errors.imei && <ErrorLabel>{t("common.required")}</ErrorLabel>}
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fields.speedAlert")}
                </InputLabel>
                <Input
                  type="number"
                  className={inputClass}
                  {...register("speed_alert")}
                />
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fields.installation")} *
                </InputLabel>
                <Input
                  type="date"
                  className={inputClass}
                  {...register("installation", { required: true })}
                />
                {errors.installation && <ErrorLabel>{t("common.required")}</ErrorLabel>}
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fields.activation")}
                </InputLabel>
                <Input
                  type="date"
                  className={inputClass}
                  {...register("activation")}
                />
              </InputContainer>
              <input type="hidden" {...register("ip")} />
              <input type="hidden" {...register("type_id")} />
            </SectionContainer>
          </Section>

          <Section>
            <SectionTitle>
              <span className={sectionSpan}>
                {t("deviceForm.sections.vehicle")}
              </span>
            </SectionTitle>
            <SectionContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fields.plates")}
                </InputLabel>
                <Input className={inputClass} {...register("plates")} />
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fields.brand")}
                </InputLabel>
                <Input className={inputClass} {...register("brand")} />
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fields.model")}
                </InputLabel>
                <Input className={inputClass} {...register("model")} />
              </InputContainer>
            </SectionContainer>
          </Section>

          <Section>
            <SectionTitle>
              <span className={sectionSpan}>
                {t("deviceForm.sections.fuel")}
              </span>
            </SectionTitle>
            <p className={`text-xs ${labelClass}`}>
              {v5Device
                ? t("deviceForm.fuel.help")
                : v5Loading
                  ? t("common.loading")
                  : t("deviceForm.fuel.noV5")}
            </p>
            <SectionContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fuel.param")}
                </InputLabel>
                <Input
                  className={`${inputClass} font-mono disabled:opacity-50`}
                  placeholder="can_fuel_level1"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={!v5Device}
                  {...fuelForm.register("fuel_sensor.param", {
                    setValueAs: (v) =>
                      (typeof v === "string" ? v.trim() : v) || undefined,
                    validate: () =>
                      fuelSensorErrors(fuelForm.getValues("fuel_sensor"))
                        .param ?? true,
                  })}
                />
                {fuelErrors?.param && (
                  <ErrorLabel>{fuelErrors.param.message}</ErrorLabel>
                )}
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fuel.empty")}
                </InputLabel>
                <Input
                  type="number"
                  step="any"
                  inputMode="decimal"
                  className={`${inputClass} disabled:opacity-50`}
                  disabled={!v5Device}
                  {...fuelForm.register("fuel_sensor.empty_raw", {
                    setValueAs: rawValue,
                    validate: () =>
                      fuelSensorErrors(fuelForm.getValues("fuel_sensor"))
                        .empty_raw ?? true,
                  })}
                />
                {fuelErrors?.empty_raw && (
                  <ErrorLabel>{fuelErrors.empty_raw.message}</ErrorLabel>
                )}
              </InputContainer>
              <InputContainer>
                <InputLabel className={labelClass}>
                  {t("deviceForm.fuel.full")}
                </InputLabel>
                <Input
                  type="number"
                  step="any"
                  inputMode="decimal"
                  className={`${inputClass} disabled:opacity-50`}
                  disabled={!v5Device}
                  {...fuelForm.register("fuel_sensor.full_raw", {
                    setValueAs: rawValue,
                    validate: () =>
                      fuelSensorErrors(fuelForm.getValues("fuel_sensor"))
                        .full_raw ?? true,
                  })}
                />
                {fuelErrors?.full_raw && (
                  <ErrorLabel>{fuelErrors.full_raw.message}</ErrorLabel>
                )}
              </InputContainer>
            </SectionContainer>
          </Section>
        </SectionsContainer>

        <ButtonsContainer>
          {!isNew && (
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-500"
              onClick={onDelete}
              disabled={deleting || saving}
            >
              {deleting ? t("deviceForm.deleting") : t("common.delete")}
            </Button>
          )}
          <Button
            type="button"
            className="bg-dark-border hover:bg-[#444444]"
            onClick={() => navigate("/sg/devices")}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? t("common.saving") : t("common.save")}
          </Button>
        </ButtonsContainer>
      </Form>
    </FormContainer>
  );
}
